import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface Organization {
  id: number;
  name: string;
  description?: string;
  currentQuota: number;
  maxAdminCount: number;
  maxWorkerCount: number;
  adminCount: number;
  workerCount: number;
  adminInviteCode?: string;
  workerInviteCode?: string;
  status: 'active' | 'suspended' | 'expired';
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
  maxAdmins?: number;
  maxWorkers?: number;
  initialQuota?: number;
  expiresAt?: string;
}

export interface OrganizationMember {
  id: number;
  username: string;
  role: 'admin' | 'worker';
  createdAt: string;
}

export interface QuotaUsageLogItem {
  id: number;
  userId: number;
  username: string;
  dataRecordId: number | null;
  quotaUsed: number;
  createdAt: string;
  orderNumber: string | null;
  title: string | null;
  model: string | null;
  cardCount: number | null;
  templateId: string | null;
}

export interface PaginationInfo {
  current: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export class OrganizationService {
  private static getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Root/Controller methods
  static async getAllOrganizations(): Promise<{ organizations: Organization[] }> {
    const response = await axios.get(
      `${API_BASE_URL}/api/organization/admin/organizations`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async getOrganizationById(id: number): Promise<{ organization: Organization }> {
    const response = await axios.get(
      `${API_BASE_URL}/api/organization/admin/organizations/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async createOrganization(data: CreateOrganizationRequest): Promise<{ organization: Organization }> {
    const response = await axios.post(
      `${API_BASE_URL}/api/organization/admin/organizations`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async addOrganizationQuota(id: number, amount: number, description?: string): Promise<{ message: string; additionId: number; amount: number }> {
    const response = await axios.put(
      `${API_BASE_URL}/api/organization/admin/organizations/${id}/quota`,
      { amount, description },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async updateOrganizationLimits(id: number, maxAdmins: number, maxWorkers: number): Promise<{ message: string }> {
    const response = await axios.put(
      `${API_BASE_URL}/api/organization/admin/organizations/${id}/limits`,
      { maxAdmins, maxWorkers },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async deleteOrganization(id: number): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_BASE_URL}/api/organization/admin/organizations/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async getOrganizationUsers(id: number): Promise<{ users: OrganizationMember[] }> {
    const response = await axios.get(
      `${API_BASE_URL}/api/organization/admin/organizations/${id}/users`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Admin user methods
  static async getOrganizationInfo(): Promise<{ organization: Omit<Organization, 'adminInviteCode' | 'workerInviteCode'> }> {
    const response = await axios.get(
      `${API_BASE_URL}/api/organization/info`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async getOrganizationMembers(): Promise<{ 
    members: OrganizationMember[];
    organization: {
      id: number;
      name: string;
      adminCount: number;
      workerCount: number;
      maxAdminCount: number;
      maxWorkerCount: number;
      adminInviteCode: string;
      workerInviteCode: string;
    };
  }> {
    const response = await axios.get(
      `${API_BASE_URL}/api/organization/members`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  static async getQuotaUsageLogs(params: {
    start: string;
    end: string;
    userIds?: number[];
    page?: number;
    limit?: number;
  }): Promise<{ logs: QuotaUsageLogItem[]; pagination: PaginationInfo }> {
    const queryParams: any = {
      start: params.start,
      end: params.end,
      page: params.page || 1,
      limit: params.limit || 200
    };
    if (params.userIds && params.userIds.length > 0) {
      queryParams.userIds = params.userIds.join(',');
    }

    const response = await axios.get(
      `${API_BASE_URL}/api/organization/quota-usage`,
      { headers: this.getAuthHeaders(), params: queryParams }
    );
    return response.data;
  }

  /**
   * @deprecated 使用 DataService.getRecords() 替代
   */
  static async getOrganizationRecords(offset = 0, limit = 10, search?: string): Promise<{
    records: any[];
    total: number;
  }> {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });
    if (search) {
      params.append('search', search);
    }

    const response = await axios.get(
      `${API_BASE_URL}/api/organization/records?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Utility methods
  static async validateInviteCode(code: string): Promise<{
    organization: {
      id: number;
      name: string;
      description?: string;
      status: string;
    };
    role: 'admin' | 'worker';
  }> {
    const response = await axios.get(`${API_BASE_URL}/api/organization/invite-code/${code}`);
    return response.data;
  }
}
